/**
 * Lightweight module-level pub/sub store for tracking denied permission checks.
 *
 * Pure JS — zero React overhead. Called synchronously from useCanDo during renders.
 * Uses Promise.resolve() to defer listener notifications past the current render,
 * avoiding the "setState during render" anti-pattern.
 *
 * Compatible with React 18's useSyncExternalStore.
 */

let _denied = [];
const _listeners = new Set();

/**
 * Report a denied permission. Deduplicates by "module::action" key.
 * Safe to call during React render.
 * @param {string} action
 * @param {string} module
 */
export const reportDenied = (action, module) => {
  const key = `${module || "?"}::${action || "?"}`;
  if (_denied.some((d) => d.key === key)) return;
  _denied = [
    ..._denied,
    {
      key,
      action: action || "?",
      module: module || "?",
      ts: Date.now(),
    },
  ];
  // Defer notifications to avoid setState-during-render
  Promise.resolve().then(() => {
    _listeners.forEach((fn) => fn(_denied));
  });
};

/** Clear all tracked denials and notify subscribers. */
export const clearDenied = () => {
  _denied = [];
  _listeners.forEach((fn) => fn(_denied));
};

/** Returns current snapshot (used by useSyncExternalStore). */
export const getDenied = () => _denied;

/**
 * Subscribe to store changes.
 * Returns unsubscribe function — compatible with useSyncExternalStore.
 * @param {function} listener
 * @returns {function}
 */
export const subscribeDenied = (listener) => {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
};
